import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeviceExchangeMutation } from '@/api/queries/auth';
import { PATH } from '@/router/PATH';
import { BRANCH_ARCHIVED } from '@/utils';

/**
 * Drives the enrollment screen: exchange the branch's static API token for a
 * device session, then move on to seller-select. The device session is
 * persisted by the mutation; on a bad / rotated token the server returns a
 * `success: false` envelope. The token is sensitive, so we never echo the
 * server's prose — we pick a fixed key instead, and SEL-19 gives
 * `BRANCH_ARCHIVED` its own: the terminal and its token are fine, the branch
 * is closed, and re-pasting the token will never help. Logic lives here so
 * `EnrollView.tsx` stays render-only.
 */
export const useEnroll = () => {
    const navigate = useNavigate();
    const exchange = useDeviceExchangeMutation();
    const [token, setTokenValue] = React.useState('');

    const error =
        exchange.data && !exchange.data.success ? exchange.data.error : null;
    const hasError = error !== null;
    const errorKey = !hasError
        ? null
        : error?.code === BRANCH_ARCHIVED
          ? 'enroll.branchArchived'
          : 'enroll.error';

    const setToken = React.useCallback(
        (value: string) => {
            setTokenValue(value);
            // Clear a previous failure as soon as the operator edits the token.
            if (exchange.data || exchange.isError) {
                exchange.reset();
            }
        },
        [exchange],
    );

    const canSubmit = token.trim().length > 0 && !exchange.isPending;

    const onSubmit = React.useCallback(async () => {
        const apiToken = token.trim();
        if (!apiToken) {
            return;
        }
        const result = await exchange.mutateAsync({ apiToken });
        if (result.success) {
            navigate(PATH.LOGIN, { replace: true });
        }
    }, [token, exchange, navigate]);

    return {
        token,
        setToken,
        onSubmit,
        canSubmit,
        hasError,
        errorKey,
        isPending: exchange.isPending,
    };
};
